import { LinkButton, Pagination, SearchInput, Text, Tooltip } from '$components'
import { useDebouncedSearch } from '$hooks/use-debounced-search'
import { EyeIcon, PlusIcon, XIcon } from '$components'
import { ElementIntent } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { PageLayout } from '$layouts/page'
import { usePage } from '$hooks/use-page'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AnimatedOutlet } from '$components'
import { AppointmentRoutePath } from '../routes/protected'
import { useMemo } from 'react'
import { ConfirmationModal } from '$components'
import { ChipCell } from '$components'
import { DateCell } from '$components'
import { DurationCell } from '$components'
import { StatusCell } from '$components'
import { Appointment, AppointmentStatus } from '$api-client/types.gen'
import { appointmentCancelMutation, appointmentIndexOptions, appointmentIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { AppointmentsFilterParam } from '../enums/filter-params'
import { Router } from '$utils/router'
import { ClientCell } from '$features/pets/components/cells/client-cell'
import { PetCell } from '$features/pets/components/cells/pet-cell'
import { useModalState } from '$components'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { Role } from '$api-client/types.gen'
import { GenericTable } from '$components'
import { OptionsCell } from '$components'
import { useNavigate } from 'react-router-dom'
import { useTableSort } from '$hooks/use-table-sort'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { PersonCell } from '$components'
import { ToastHelper } from '$components'
import { usePageTitle } from '$components'
import { NoAppointmentsEmptyState } from '../components/no-appointments-empty-state'
import { TableSkeleton } from '$components'
import { Filters, useFilters } from '$features/filtering'
import { Filter } from '$features/filtering/types'
import { useInfiniteClientOptions } from '$hooks/use-infinite-client-options'
import { useInfiniteGroomerOptions } from '$hooks/use-infinite-groomer-options'
import { useInfinitePetOptions } from '$hooks/use-infinite-pet-options'

export type CancelAppointmentModalState = {
  isOpen: boolean,
  appointmentId: number | null,
}

const columnHelper = createColumnHelper<Appointment>()

export type AppointmentsPageProps = Record<string, never>

export const AppointmentsPage = () => {
  usePageTitle('Appointments')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { page, setPage } = usePage()
  const { user } = useAuth()
  const { getSorting } = useTableSort()
  const { hasSubscriptionExpired } = useTenant()
  const { getFiltersString } = useFilters()
  const clientOptions = useInfiniteClientOptions()
  const groomerOptions = useInfiniteGroomerOptions()
  const petOptions = useInfinitePetOptions()

  const { search, setSearch, debouncedSearch } = useDebouncedSearch({
    searchParam: AppointmentsFilterParam.Search,
    initialValue: '',
    onSearchChange: () => setPage(1),
  })

  const filters: Filter[] = [
    {
      id: AppointmentsFilterParam.Status,
      label: 'Status',
      type: 'select',
      options: [
        { id: 'Pending', label: 'Pending', value: 'Pending' },
        { id: 'Confirmed', label: 'Confirmed', value: 'Confirmed' },
        { id: 'Completed', label: 'Completed', value: 'Completed' },
        { id: 'No Show', label: 'No Show', value: 'No Show' },
        { id: 'Cancelled', label: 'Cancelled', value: 'Cancelled' },
      ],
    },
    {
      id: AppointmentsFilterParam.Client,
      label: 'Client',
      type: 'combobox',
      multiple: false,
      state: clientOptions,
    },
    {
      id: AppointmentsFilterParam.Pet,
      label: 'Pet',
      type: 'combobox',
      multiple: false,
      state: petOptions,
    },
    {
      id: AppointmentsFilterParam.Groomer,
      label: 'Groomer',
      type: 'combobox',
      multiple: false,
      state: groomerOptions,
    },
    {
      id: AppointmentsFilterParam.Date,
      label: 'Date Range',
      type: 'date-range',
    },
  ]

  const { modalState: cancelModalState, close: closeCancelModal, open: openCancelModal } = useModalState()

  const { data: appointments, isLoading: isAppointmentsLoading } = useQuery({
    ...appointmentIndexOptions({
      query: {
        query: debouncedSearch,
        page: page,
        sort: getSorting(),
        filters: getFiltersString(),
        approved: 'approved',
      },
    }),
  })

  const { mutate: cancelAppointment, isPending: isCancellingAppointment } = useMutation({
    ...appointmentCancelMutation(),
    onSuccess: () => {
      ToastHelper.success('Appointment cancelled successfully.')
      queryClient.invalidateQueries({ queryKey: appointmentIndexQueryKey() })
      closeCancelModal()
    },
    onError: () => ToastHelper.error('Failed to cancel appointment.'),
  })

  const handleCancelAppointment = () => {
    cancelAppointment({
      path: {
        id: cancelModalState.modelId!,
      },
    })
  }

  const columns = [
    columnHelper.accessor('start_at', {
      header: 'Date',
      cell: ({ getValue }) => <DateCell date={getValue()} />,
    }),
    columnHelper.accessor('client', {
      header: 'Client',
      cell: ({ row }) => <ClientCell data={row.original.client} />,
    }),
    columnHelper.accessor('pet', {
      header: 'Pet',
      cell: ({ row }) => <PetCell data={row.original.pet} />,
    }),
    columnHelper.accessor('services', {
      header: 'Services',
      cell: ({ getValue }) => {
        const services = getValue()
        return <ChipCell values={services.map(service => service.service_values.name)} />
      },
    }),
    columnHelper.accessor('start_at', {
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => (
        <DurationCell
          startDate={row.original.start_at}
          endDate={row.original.finish_at}
        />
      ),
    }),
    columnHelper.accessor('groomer', {
      header: 'Groomer',
      cell: ({ row }) => <PersonCell data={row.original.groomer} />,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => <StatusCell status={getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      maxSize: 66,
      minSize: 66,
      cell: ({ row }) => (
        <OptionsCell
          options={[
            {
              id: 'view',
              value: 'View',
              icon: EyeIcon,
              onClick: () => navigate(Router.getRoute(AppointmentRoutePath.AppointmentsView, { id: row.original.id })),
            },
            {
              id: 'cancel',
              value: 'Cancel',
              intent: ElementIntent.Danger,
              onClick: () => openCancelModal(row.original.id),
              icon: XIcon,
              disabled: row.original.status === AppointmentStatus.CANCELLED || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
              tooltip: row.original.status === AppointmentStatus.CANCELLED
                ? 'This appointment has already been cancelled.'
                : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                  ? 'You are not allowed to cancel appointments.'
                  : undefined,
            },
          ]}
        />
      ),
    }),
  ]

  const data = useMemo(() => appointments?.data ?? [], [appointments])

  return (
    <PageLayout>
      {/* Header */}
      <div className="grid grid-cols-1 grid-rows-[auto_1fr] h-full">
        <div className="pb-stack-roomy flex sm:flex-row flex-col sm:items-center justify-between gap-stack-roomy sm:gap-stack-tight">
          <Text
            as="h1"
            weight="medium"
          >
            Appointments
          </Text>

          <div className="flex gap-stack-tight items-center">
            <Filters filters={filters} />

            <SearchInput
              value={search}
              onChange={setSearch}
              className="w-full sm:w-52"
            />

            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                to={Router.getRoute(AppointmentRoutePath.AppointmentsCreate)}
                icon={PlusIcon}
                disabled={hasSubscriptionExpired}
              >
                New Appointment
              </LinkButton>
            </Tooltip>
          </div>
        </div>

        {/* Page Content */}
        {isAppointmentsLoading && (
          <TableSkeleton
            columns={7}
            rows={10}
          />
        )}

        {!isAppointmentsLoading && data.length === 0 && (
          <NoAppointmentsEmptyState isNoResults={debouncedSearch.length > 0} />
        )}

        {data.length > 0 && (
          <GenericTable.Container>
            <GenericTable
              data={data}
              columns={columns}
              rowLink={row => Router.getRoute(AppointmentRoutePath.AppointmentsView, { id: row.id })}
            />

            <Pagination
              currentPage={page}
              onChange={setPage}
              totalPages={appointments?.meta.last_page}
              totalItems={appointments?.meta.total}
              itemsPerPage={appointments?.meta.per_page}
              currentItemCount={appointments?.meta.current_page}
            />
          </GenericTable.Container>
        )}
      </div>

      <AnimatedOutlet />

      {cancelModalState.isOpen && (
        <ConfirmationModal
          intent={ElementIntent.Danger}
          onClose={closeCancelModal}
          onConfirm={handleCancelAppointment}
          isLoading={isCancellingAppointment}
          title="Cancel Appointment"
          description="Are you sure you want to cancel this appointment?"
        />
      )}
    </PageLayout>
  )
}
